import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { LanguageModal } from '@/components/LanguageModal';
import { PageHeader } from '@/components/PageHeader';
import { Text } from '@/components/Text';
import { useSubscription } from '@/hooks/useSubscription';
import { SeatsModal } from '@/partials/dashboard/Pricing/SeatsModal';
import { AuthUserProfile } from '@/partials/dashboard/Users/AuthUserProfile';
import { AuthUserSettings } from '@/partials/dashboard/Users/AuthUserSettings';
import { SubscriptionCTA } from '@/partials/dashboard/Users/SubscriptionCTA';
import { subscribe } from '@/services/stripeService';
import { useAuthStore } from '@/stores/useAuthStore';
import { handleError } from '@/utils/handleError';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaChevronRight,
  FaGlobe,
  FaQuestionCircle,
  FaShieldAlt,
  FaSignOutAlt,
  FaUser,
} from 'react-icons/fa';

type TabType = 'profile' | 'security' | 'language' | 'support' | 'menu';

interface MenuItem {
  id: TabType;
  icon: React.ReactNode;
  label: string;
  description: string;
  component?: React.ReactNode;
}

export const Settings = () => {
  const { t } = useTranslation();
  const { logout, user } = useAuthStore();
  const { hasActiveSubscription } = useSubscription(user ?? null);
  const [activeTab, setActiveTab] = useState<TabType>('menu');
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isSeatsModalOpen, setIsSeatsModalOpen] = useState(false);

  // Plus plan data
  const plusPlan = {
    id: 'business-plus',
    monthly: 8.99,
    yearly: 7.49,
    monthly_plan_id: 1,
    yearly_plan_id: 2,
  };

  // Language component placeholder - you can implement this later
  const LanguageSettings = () => (
    <div className="space-y-6">
      <div>
        <Text className="mb-2 text-lg font-semibold text-gray-900">
          Language Preferences
        </Text>
        <Text className="mb-4 text-gray-600">
          Choose your preferred language for the application
        </Text>
        <div className="grid grid-cols-2 gap-3">
          {['English', 'Français', 'Deutsch', 'Nederlands'].map(lang => (
            <Button
              key={lang}
              className={`justify-start border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ${lang === 'English' ? 'border-sky-500 bg-sky-50 text-sky-700' : ''}`}
            >
              <FaGlobe className="mr-2" />
              {lang}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );

  // Support component placeholder
  const SupportSettings = () => (
    <div className="space-y-6">
      <div>
        <Text className="mb-2 text-lg font-semibold text-gray-900">
          {t('settings:support.title')}
        </Text>
        <Text className="mb-6 text-gray-600">
          {t('settings:support.subtitle')}
        </Text>

        <div className="grid gap-4">
          <div
            className="cursor-pointer"
            onClick={() =>
              window.open('https://linko-connect.com/contact/', '_blank')
            }
          >
            <Card className="p-4 transition-shadow hover:shadow-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-blue-100 p-2">
                    <FaQuestionCircle className="text-linko-light" />
                  </div>
                  <div>
                    <Text className="font-medium">
                      {t('settings:support.faq.title')}
                    </Text>
                    <Text className="text-sm text-gray-500">
                      {t('settings:support.faq.description')}
                    </Text>
                  </div>
                </div>
                <FaChevronRight className="text-gray-400" />
              </div>
            </Card>
          </div>

          <div
            className="cursor-pointer"
            onClick={() =>
              window.open('https://linko-connect.com/contact/', '_blank')
            }
          >
            <Card className="p-4 transition-shadow hover:shadow-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-green-100 p-2">
                    <FaUser className="text-green-600" />
                  </div>
                  <div>
                    <Text className="font-medium">
                      {t('settings:support.contact.title')}
                    </Text>
                    <Text className="text-sm text-gray-500">
                      {t('settings:support.contact.description')}
                    </Text>
                  </div>
                </div>
                <FaChevronRight className="text-gray-400" />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  const menuItems: MenuItem[] = [
    {
      id: 'profile',
      icon: <FaUser className="size-6" />,
      label: t('settings:menu.profile.label'),
      description: t('settings:menu.profile.description'),
      component: <AuthUserProfile />,
    },
    {
      id: 'security',
      icon: <FaShieldAlt className="size-6" />,
      label: t('settings:menu.security.label'),
      description: t('settings:menu.security.description'),
      component: <AuthUserSettings />,
    },
    {
      id: 'language',
      icon: <FaGlobe className="size-6" />,
      label: t('settings:menu.language.label'),
      description: t('settings:menu.language.description'),
      component: <LanguageSettings />,
    },
    {
      id: 'support',
      icon: <FaQuestionCircle className="size-6" />,
      label: t('settings:menu.support.label'),
      description: t('settings:menu.support.description'),
      component: <SupportSettings />,
    },
  ];

  const handleMenuClick = (tabId: TabType) => {
    if (tabId === 'language') {
      setIsLanguageModalOpen(true);
    } else if (tabId === 'support') {
      window.open('https://linko-connect.com/contact/', '_blank');
    } else {
      setActiveTab(tabId);
    }
  };

  const handleBackClick = () => {
    setActiveTab('menu');
  };

  // Subscription logic
  const handleSubscribe = async (isYearly: boolean) => {
    try {
      // Check if user is manager or admin and has a company
      if (
        (user?.role === 'manager' || user?.role === 'admin') &&
        user?.company?.id
      ) {
        // Show seats modal
        setIsSeatsModalOpen(true);
        return;
      }

      // For regular users, proceed directly to subscription
      await proceedToSubscription(isYearly);
    } catch (error) {
      handleError(error);
    }
  };

  const proceedToSubscription = async (
    isYearly: boolean,
    seats?: number | null,
  ) => {
    try {
      const planId = isYearly
        ? plusPlan.yearly_plan_id
        : plusPlan.monthly_plan_id;
      const interval = isYearly ? 'yearly' : 'monthly';

      if (!planId) {
        return;
      }

      const { data } = await subscribe(planId, interval, seats ?? undefined);

      window.location.href = data.checkout_url ?? data.portal;
    } catch (error) {
      handleError(error);
    }
  };

  const handleSeatsConfirm = (seats: number) => {
    proceedToSubscription(true, seats); // Default to yearly for seats modal
    setIsSeatsModalOpen(false);
  };

  // Render individual page view
  if (activeTab !== 'menu') {
    const currentItem = menuItems.find(item => item.id === activeTab);

    return (
      <div className="h-full space-y-6 overflow-y-auto">
        {/* Header */}
        <motion.div
          className="flex flex-col gap-4 p-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header avec bouton retour */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              <FaArrowLeft className="size-4" />
              {t('common:back')}
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-lg font-semibold text-secondary">
              {currentItem?.label}
            </h1>
          </div>
        </motion.div>

        {/* Contenu */}
        <motion.div
          className="px-4 pb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {currentItem?.component}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Modals */}
        <LanguageModal
          isOpen={isLanguageModalOpen}
          onClose={() => setIsLanguageModalOpen(false)}
        />
        <SeatsModal
          isOpen={isSeatsModalOpen}
          onClose={() => setIsSeatsModalOpen(false)}
          onConfirm={handleSeatsConfirm}
          planName="Business Plus"
          price={plusPlan.yearly}
          isYearly={true}
        />
      </div>
    );
  }

  // Render main menu view
  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PageHeader title={t('settings:title')} />
        <Text className="text-center text-gray-600">
          {t('settings:subtitle')}
        </Text>
      </motion.div>

      {/* Subscription CTA */}
      {(!hasActiveSubscription() || user?.seats?.length === 0) && (
        <motion.div
          className="px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <SubscriptionCTA
            onSubscribe={handleSubscribe}
            plusPlan={{
              monthly: plusPlan.monthly,
              yearly: plusPlan.yearly,
            }}
          />
        </motion.div>
      )}

      {/* Menu Items */}
      <motion.div
        className="px-4 pb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="space-y-4">
          {/* Settings Menu Items */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {menuItems.map((item, index) => (
              <motion.button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className="group rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:shadow-md md:p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="rounded-lg bg-sky-100 p-3 transition-colors group-hover:bg-sky-200">
                      {item.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Text className="mb-1 font-semibold text-gray-900 group-hover:text-sky-700">
                        {item.label}
                      </Text>
                      <Text className="text-sm text-gray-500">
                        {item.description}
                      </Text>
                    </div>
                  </div>
                  <FaChevronRight className="mt-2 text-gray-400 transition-colors group-hover:text-sky-500" />
                </div>
              </motion.button>
            ))}
          </div>

          {/* Logout Button */}
          <motion.button
            onClick={logout}
            className="group w-full rounded-2xl border border-red-100 bg-red-50 p-4 text-left shadow-sm transition-all duration-200 hover:border-red-200 hover:bg-red-100 hover:shadow-md md:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.7 }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <div className="flex items-center space-x-4">
              <div className="rounded-lg bg-red-200 p-3 transition-colors group-hover:bg-red-300">
                <FaSignOutAlt className="size-6 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <Text className="mb-1 font-semibold text-red-700">
                  {t('settings:logout.title')}
                </Text>
                <Text className="text-sm text-red-600">
                  {t('settings:logout.description')}
                </Text>
              </div>
            </div>
          </motion.button>
        </div>
      </motion.div>

      {/* Modals */}
      <LanguageModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
      />
      <SeatsModal
        isOpen={isSeatsModalOpen}
        onClose={() => setIsSeatsModalOpen(false)}
        onConfirm={handleSeatsConfirm}
        planName="Business Plus"
        price={plusPlan.yearly}
        isYearly={true}
      />
    </div>
  );
};
